import { useEffect, useRef } from 'react';

/**
 * Accessible focus-trap for modal-heavy modules (PLM, Finance,
 * Sustainability, Digital Twin, Predictive, Workflow, Alarms, Scada).
 *
 * Returns a ref the consumer attaches to the container that should
 * trap Tab/Shift-Tab. When `active` is true, focus is moved to the
 * first focusable element on mount and cycles within the container.
 * Escape calls `onClose` (if provided). Restores focus to the
 * previously-active element on unmount.
 *
 * Radix's Dialog already provides a built-in focus trap for typed
 * modals; this hook covers the cases Radix doesn't handle — popovers
 * with custom content, side-panel pages, and non-Dialog modal-feeling
 * surfaces (e.g. multi-step wizards rendered inline).
 *
 * @example
 * const trapRef = useFocusTrap<HTMLDivElement>({ active: open, onClose: () => setOpen(false) });
 * return open ? <div ref={trapRef} role="dialog" aria-modal="true">…</div> : null;
 */
export function useFocusTrap<T extends HTMLElement = HTMLElement>(opts: {
  active: boolean;
  onClose?: () => void;
}) {
  const ref = useRef<T | null>(null);
  const { active, onClose } = opts;

  useEffect(() => {
    if (!active) return;
    const container = ref.current;
    if (!container) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;

    const focusables = (): HTMLElement[] => {
      const selector = [
        'a[href]',
        'area[href]',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        'button:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(',');
      return Array.from(container.querySelectorAll<HTMLElement>(selector)).filter(
        (el) => !el.hasAttribute('hidden') && el.offsetParent !== null,
      );
    };

    // Move initial focus.
    const first = focusables()[0];
    if (first) first.focus();
    else container.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose?.();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = focusables();
      const firstEl = items[0];
      const lastEl = items[items.length - 1];
      if (!firstEl || !lastEl) return;
      const active = document.activeElement as HTMLElement | null;

      if (e.shiftKey && active === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && active === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };

    container.addEventListener('keydown', onKey);
    return () => {
      container.removeEventListener('keydown', onKey);
      // Best-effort focus restore.
      if (previouslyFocused && document.body.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
  }, [active, onClose]);

  return ref;
}
